import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Copy, ExternalLink, GraduationCap } from "lucide-react";

export default function EduFair() {
  const [options, setOptions] = useState([]);
  const qrRef = useRef(null);
  const landingUrl = `${window.location.origin}/edu`;

  useEffect(() => { api.get("/public/edu/options").then((r) => setOptions(r.data)).catch(() => {}); }, []);

  const download = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "DIGI-LOANS-Education-Fair-QR.png";
    a.click();
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(landingUrl);
      toast.success("Landing link copied");
    } catch (e) {
      toast.error("Couldn't copy — long-press the link to copy it manually");
    }
  };

  return (
    <div data-testid="edu-fair-admin-page">
      <PageHeader title="Education Fair QR" subtitle="Print this QR for the fair. Students scan it, pick an option, and their details are captured before they reach the link." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-8 flex flex-col items-center text-center">
          <div className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2"><GraduationCap size={18} className="text-[#0F4C81]" />Scan to open the Education Loan menu</div>
          <div className="bg-white p-4 rounded-2xl border-2 border-slate-200" ref={qrRef} data-testid="edu-qr">
            <QRCodeCanvas value={landingUrl} size={260} level="M" includeMargin />
          </div>
          <p className="text-xs text-slate-500 break-all mt-4 max-w-xs">{landingUrl}</p>
          <div className="flex gap-2 mt-4">
            <Button onClick={download} className="bg-[#0F4C81] hover:bg-[#0A3A66]" data-testid="edu-qr-download"><Download size={16} className="mr-2" />Download PNG</Button>
            <Button variant="outline" onClick={copy} data-testid="edu-qr-copy"><Copy size={16} className="mr-2" />Copy Link</Button>
          </div>
        </Card>

        <Card className="p-8">
          <div className="text-sm font-bold text-slate-700 mb-4">The 3 options students will see</div>
          <div className="space-y-3">
            {options.map((o, i) => (
              <a key={o.id} href={o.url} target="_blank" rel="noreferrer"
                className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors" data-testid={`edu-admin-link-${o.id}`}>
                <div className="w-8 h-8 rounded-lg bg-[#0A192F] text-emerald-400 flex items-center justify-center font-bold text-sm">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800">{o.label}</div>
                  <div className="text-xs text-slate-400 truncate">{o.url}</div>
                </div>
                <ExternalLink size={16} className="text-slate-400 shrink-0" />
              </a>
            ))}
          </div>
          <div className="mt-5 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg p-3">
            Every scan-and-click captures the student's and parent's details (with parent profession) as a lead under <b>source: Education Fair</b> in your Customer Database — before the student is redirected.
          </div>
        </Card>
      </div>
    </div>
  );
}
